import { useEffect, useState } from 'react';
import type { TrustedDeviceResponse } from '@printtat/shared';
import { Alert, Button } from '@printtat/ui';
import { Monitor, Trash2 } from 'lucide-react';
import { apiFetch, apiJson } from '@/lib/apiFetch';

const formatDate = (iso: string) => new Date(iso).toLocaleString();

const summarizeUserAgent = (ua: string | null): string => {
  if (!ua) return 'Unknown device';
  if (/iPhone|iPad/.test(ua)) return 'iPhone / iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua))
    return /Chrome/.test(ua)
      ? 'Chrome on Mac'
      : /Safari/.test(ua)
        ? 'Safari on Mac'
        : 'Mac';
  if (/Windows/.test(ua))
    return /Chrome/.test(ua) ? 'Chrome on Windows' : 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return ua.slice(0, 60);
};

export const TrustedDevicesList = () => {
  const [devices, setDevices] = useState<TrustedDeviceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson<TrustedDeviceResponse[]>(
        '/auth/trusted-devices',
      );
      setDevices(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load trusted devices.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const revoke = async (id: string) => {
    setRevoking(id);
    setError(null);
    try {
      await apiFetch(`/auth/trusted-devices/${id}/revoke`, { method: 'POST' });
      setDevices((ds) => ds.filter((d) => d.id !== id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to revoke device.',
      );
    } finally {
      setRevoking(null);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (devices.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {error ? <Alert variant="error">{error}</Alert> : null}
        <p className="text-sm text-gray-500">
          No trusted devices. When you sign in with 2FA you can check
          &ldquo;Trust this device for 7 days&rdquo; to skip the code on this
          browser.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {error ? <Alert variant="error">{error}</Alert> : null}
      <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
      {devices.map((d) => (
        <li key={d.id} className="flex items-center gap-3 px-3 py-2.5">
          <Monitor className="w-4 h-4 text-gray-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900 truncate">
              {summarizeUserAgent(d.userAgent)}
            </p>
            <p className="text-xs text-gray-500">
              {d.ipAddress ?? 'unknown IP'} · last used{' '}
              {formatDate(d.lastUsedAt)} · expires {formatDate(d.expiresAt)}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => revoke(d.id)}
            disabled={revoking === d.id}
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <Trash2 size={12} className="mr-1.5" />
            {revoking === d.id ? 'Revoking…' : 'Revoke'}
          </Button>
        </li>
      ))}
      </ul>
    </div>
  );
};
